"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import type { MutableRefObject } from "react";
import ReactMarkdown from "react-markdown";
import type { ChatMessage, Competency } from "@/lib/types";
import { useLiveApi } from "@/hooks/use-live-api";
import { useWebcamRecorder } from "@/hooks/useWebcamRecorder";
import { useI18n } from "@/lib/i18n";
import { getLiveSystemPrompt } from "@/lib/prompts";
import { posthog } from "@/lib/posthog";
import { classifyError, truncErrorMessage } from "@/lib/analytics";

type VoiceState = "idle" | "listening" | "processing" | "speaking" | "live";

/**
 * Animated 5-bar mic level indicator. Reads live amplitude from a ref on
 * requestAnimationFrame and writes DOM directly — no React re-renders, so
 * parent state stays calm during 20-30fps updates.
 *
 * Colors: active ("listening") = brand blue; inactive = the strong border grey.
 * A flat reading means the mic is dead (common on laptops) — users see this
 * and know to check permissions / USB / bluetooth headset.
 */
function MicLevelBars({
  levelRef,
  active,
}: {
  levelRef: MutableRefObject<{ level: number }>;
  active: boolean;
}) {
  const barRefs = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const level = levelRef.current?.level ?? 0;
      for (let i = 0; i < 5; i++) {
        const bar = barRefs.current[i];
        if (!bar) continue;
        // Middle bar reacts more than edges — gives a natural EQ-like shape.
        const centerDist = Math.abs(i - 2);
        const factor = 1 - centerDist * 0.18;
        const effective = Math.max(level, 0.04) * factor;
        bar.style.height = `${20 + effective * 80}%`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [levelRef]);

  const color = active ? "bg-brand" : "bg-line-strong";
  return (
    <div className="flex items-center justify-center gap-[3px] h-5 w-14" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          ref={(el) => {
            barRefs.current[i] = el;
          }}
          className={`w-[3px] rounded-full ${color} transition-colors duration-200`}
          style={{ height: "20%" }}
        />
      ))}
    </div>
  );
}

export default function InterviewChat({
  competencies,
  initialHistory,
  onHistoryUpdate,
  onComplete,
  isPaid = false,
  onGoHome,
  onCancel,
  sessionId = null,
  forcedLocale,
  timeLimitSeconds,
  interviewId = null,
}: {
  competencies: Competency[];
  initialHistory?: ChatMessage[];
  onHistoryUpdate?: (history: ChatMessage[]) => void;
  onComplete: (history: ChatMessage[], meta?: { webcamRecordingUrl?: string | null }) => void;
  isPaid?: boolean;
  onGoHome?: () => void;
  // Pre-start "Back" button. Falls back to onGoHome when omitted so older
  // callers still get a working button. Distinct from onGoHome because the
  // active-interview Home logo (top-left) does a full reset, but Back from
  // the pre-start screen should preserve JD/competencies on supporting flows.
  onCancel?: () => void;
  sessionId?: string | null;
  forcedLocale?: "en" | "ru" | "ua" | "pl" | "he";
  timeLimitSeconds?: number; // override default timer (free=300, paid=unlimited)
  interviewId?: string | null;
}) {
  const [history, setHistory] = useState<ChatMessage[]>(initialHistory ?? []);
  const { t, locale: i18nLocale } = useI18n();
  const locale = forcedLocale ?? i18nLocale;
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState((initialHistory?.length ?? 0) > 0);
  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Tracks scrollHeight from the previous render so the auto-scroll effect
  // can decide whether the user was at the bottom BEFORE new content was
  // appended. Measuring after the render is wrong: a fresh AI reply taller
  // than 150px instantly pushes `distance` over the threshold and the chat
  // stops following the conversation.
  const prevScrollHeightRef = useRef(0);
  const vadRef = useRef<any>(null);
  const historyRef = useRef<ChatMessage[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  const processingRef = useRef(false);
  const stoppedRef = useRef(false);
  const speakingRef = useRef(false);
  const completedRef = useRef(false);
  // Live mic RMS for the on-screen indicator — written by VAD onFrameProcessed,
  // read by <MicLevelBars /> via requestAnimationFrame. Ref, not state, so the
  // 20-30fps updates don't re-render the whole chat.
  const micLevelRef = useRef({ level: 0 });
  // Parallel rolling buffer of raw frames. When user presses "I'm done" (e.g.
  // background TV is holding VAD in speech mode forever), we flush whatever we
  // have here instead of waiting for VAD's onSpeechEnd.
  const rollingBufferRef = useRef<Float32Array[]>([]);
  const rollingSamplesRef = useRef(0);
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [liveModelText, setLiveModelText] = useState("");
  const liveHistoryRef = useRef<ChatMessage[]>([]);
  const liveUserTextRef = useRef("");
  const [timeLeft, setTimeLeft] = useState(
    timeLimitSeconds !== undefined ? timeLimitSeconds : isPaid ? -1 : 300
  ); // custom limit, or 5 min for free, -1 = unlimited
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showTimeCutoff, setShowTimeCutoff] = useState(false);
  // Streaming reply state — displayed in UI but NOT in history until finalized
  const [streamingUser, setStreamingUser] = useState<string | null>(null);
  const [streamingReply, setStreamingReply] = useState<string | null>(null);
  // Transient voice error — shown as a banner with Retry, NOT written to history
  // (so Gemini doesn't see the error as a past interviewer turn on the next call).
  const [lastError, setLastError] = useState<string | null>(null);
  const lastAudioRef = useRef<{ audioBase64: string; mimeType: string } | null>(null);

  const [micPermission, setMicPermission] = useState<"unknown" | "denied" | "granted">("unknown");
  const [showMicPopup, setShowMicPopup] = useState(false);

  // --- Webcam Recording ---
  const { status: webcamStatus, error: webcamError, attachVideo: webcamAttachVideo, startCamera, stopAndUpload: stopAndUploadWebcam, cleanup: cleanupWebcam } = useWebcamRecorder();
  const [showCameraPrompt, setShowCameraPrompt] = useState(false);
  const [showGdprConsent, setShowGdprConsent] = useState(false);

  // --- Avatar ---
  // old_avatar.png is the always-visible base. The old_avatar.webm/.mp4
  // loop is overlaid only while voiceState === "speaking" so the photo
  // "talks" during AI replies and snaps back to a still frame when
  // listening. These are the original avatar assets pulled out of the
  // pre-HR-replacement history (commits 9e31279 / 0a8e0eb); hr.png and
  // cut.* are no longer referenced.
  const aiAvatarVideoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);

  // Use a callback ref so the loadeddata listener is attached when the
  // <video> element first mounts (which only happens after `started`
  // becomes true — the early-return pre-start screen doesn't render it).
  const aiAvatarVideoCallbackRef = useCallback((node: HTMLVideoElement | null) => {
    // Detach from previous node (React may swap elements)
    const prev = aiAvatarVideoRef.current;
    if (prev) {
      prev.removeEventListener("loadeddata", () => {});
      prev.removeEventListener("error", () => {});
    }
    aiAvatarVideoRef.current = node;
    if (!node) {
      setVideoReady(false);
      return;
    }
    const onLoaded = () => setVideoReady(true);
    const onError = () => setVideoReady(false);
    node.addEventListener("loadeddata", onLoaded);
    node.addEventListener("error", onError);
    // Already loaded (cached by browser)
    if (node.readyState >= 2) setVideoReady(true);
  }, []);

  useEffect(() => {
    const video = aiAvatarVideoRef.current;
    if (!video || !videoReady) return;
    if (voiceState === "speaking") {
      video.currentTime = 0;
      video.play().catch(() => {
        // Autoplay can be blocked until first user gesture — fall back to
        // the static photo silently.
      });
    } else {
      video.pause();
    }
  }, [voiceState, videoReady]);

  useEffect(() => {
    historyRef.current = history;
    onHistoryUpdate?.(history);
  }, [history, onHistoryUpdate]);

  // Countdown timer (free users: 5min, kolleno: 45min, paid: unlimited)
  useEffect(() => {
    if (!started || timeLeft < 0) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          setShowTimeCutoff(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [started, isPaid, timeLeft < 0]);

  // PostHog heartbeat — keeps session recording "active" during voice interviews
  useEffect(() => {
    if (!started) return;
    const hb = setInterval(() => {
      posthog.capture("interview_heartbeat", { interview_id: interviewId, voiceState });
    }, 30_000);
    return () => clearInterval(hb);
  }, [started, voiceState]);

  // Anti-cheat: block copy/cut/context-menu/select-all while the interview screen
  // is mounted so candidates can't paste questions into ChatGPT. This doesn't
  // stop a determined user (DevTools, screenshots), but blocks lazy copy-paste.
  useEffect(() => {
    const block = (e: Event) => e.preventDefault();
    const blockKeys = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && ["c", "x", "a", "C", "X", "A"].includes(e.key)) e.preventDefault();
    };
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("contextmenu", block);
    document.addEventListener("keydown", blockKeys);
    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("keydown", blockKeys);
    };
  }, []);

  // Idempotent wrapper — three exit paths (End button, time cutoff, INTERVIEW_COMPLETE)
  // can all race. Without this guard onComplete fires 3-4× per session, generating
  // duplicate reports and inflating PostHog funnel metrics.
  const safeOnComplete = useCallback(async (finalHistory: ChatMessage[]) => {
    if (completedRef.current) return;
    completedRef.current = true;
    // Upload webcam recording BEFORE notifying parent, so the URL is available
    let webcamRecordingUrl: string | null = null;
    if (webcamStatus === 'active') {
      try {
        webcamRecordingUrl = await stopAndUploadWebcam();
      } catch (e) {
        // Upload failed — continue without URL, but log so it's not silent.
        console.warn("[interview] webcam recording upload failed:", e);
      }
    }
    onComplete(finalHistory, webcamRecordingUrl ? { webcamRecordingUrl } : undefined);
  }, [onComplete, webcamStatus, stopAndUploadWebcam]);

  // When time runs out, end the interview
  useEffect(() => {
    if (!showTimeCutoff) return;
    // Stop all audio/mic
    if (audioRef.current) { audioRef.current.onended = null; audioRef.current.onerror = null; audioRef.current.pause(); audioRef.current.src = ""; audioRef.current = null; }
    if (isLiveMode) {
      disconnectLive();
      audioContextRef.current?.close();
      audioContextRef.current = null;
    } else {
      if (vadRef.current) { try { vadRef.current.pause(); vadRef.current.destroy(); } catch {} vadRef.current = null; }
    }
    // Generate partial report after a short delay
    const finalHistory = isLiveMode ? liveHistoryRef.current : historyRef.current;
    if (finalHistory.length > 0) {
      setTimeout(() => safeOnComplete(finalHistory), 500);
    }
  }, [showTimeCutoff]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const prevHeight = prevScrollHeightRef.current;
    // First render: nothing to compare against, treat as "follow".
    const wasAtBottom =
      prevHeight === 0 || prevHeight - el.scrollTop - el.clientHeight < 150;
    prevScrollHeightRef.current = el.scrollHeight;
    if (wasAtBottom) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [history, voiceState, loading, streamingReply, liveModelText, lastError]);

  // Audio queue handling for Live API
  const playAudioChunk = useCallback((float32: Float32Array) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
    }
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const buffer = ctx.createBuffer(1, float32.length, 24000);
    buffer.copyToChannel(float32 as unknown as Float32Array<ArrayBuffer>, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    // Schedule next chunk at the end of the previous one
    // Add a small buffer if we fell behind (latency catch-up)
    const startTime = Math.max(now, nextStartTimeRef.current);
    source.start(startTime);
    nextStartTimeRef.current = startTime + buffer.duration;
  }, []);

  // Live API: accumulate output transcription (what model actually says, not thinking)
  const onLiveTextData = useCallback((text: string) => {
    setLiveModelText(prev => prev + text);
  }, []);

  // When user speaks in live mode, accumulate words (don't create a message per word)
  const onLiveUserSpeech = useCallback((text: string) => {
    if (!text.trim()) return;
    liveUserTextRef.current += text;
  }, []);

  // When model turn completes: flush accumulated user text + model text as chat messages
  const onLiveTurnComplete = useCallback(() => {
    // Flush any accumulated user speech first
    const userText = liveUserTextRef.current.trim();
    if (userText) {
      const userMsg: ChatMessage = { role: "user", parts: [{ text: userText }] };
      liveHistoryRef.current = [...liveHistoryRef.current, userMsg];
      liveUserTextRef.current = "";
    }

    // Flush accumulated model text
    setLiveModelText(prev => {
      if (prev.trim()) {
        const botMsg: ChatMessage = { role: "model", parts: [{ text: prev.trim() }] };
        liveHistoryRef.current = [...liveHistoryRef.current, botMsg];
      }
      setHistory([...liveHistoryRef.current]);
      return "";
    });
  }, []);

  const { connect: connectLive, disconnect: disconnectLive, state: liveState } = useLiveApi({
    onAudioData: playAudioChunk,
    onTextData: onLiveTextData,
    onTurnComplete: onLiveTurnComplete,
    onUserSpeech: onLiveUserSpeech,
  });

  // Cleanup VAD + audio on unmount (e.g. when switching to report)
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Auto-start VAD if restoring a session or starting in Standard Mode
  useEffect(() => {
    if (started && !vadRef.current && !isLiveMode) {
      startVAD();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, isLiveMode]);

  useEffect(() => {
    return () => {
      if (vadRef.current) {
        vadRef.current.destroy();
        vadRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
      if (timerRef.current) clearInterval(timerRef.current);
      disconnectLive();
      audioContextRef.current?.close();
      cleanupWebcam();
    };
  }, []);

  // Build a WAV blob from raw PCM (16-bit LE, mono)
  function pcmToWavBlob(pcmBase64: string, sampleRate: number): Blob {
    const raw = atob(pcmBase64);
    const pcmBytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) pcmBytes[i] = raw.charCodeAt(i);
    const header = new ArrayBuffer(44);
    const v = new DataView(header);
    const writeStr = (off: number, s: string) => {
      for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i));
    };
    writeStr(0, "RIFF"); v.setUint32(4, 36 + pcmBytes.length, true);
    writeStr(8, "WAVE"); writeStr(12, "fmt "); v.setUint32(16, 16, true);
    v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, sampleRate, true);
    v.setUint32(28, sampleRate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
    writeStr(36, "data"); v.setUint32(40, pcmBytes.length, true);
    return new Blob([header, pcmBytes], { type: "audio/wav" });
  }

  // Helper to convert VAD float32 to base64
  function float32ToWavBase64(float32: Float32Array, sampleRate: number): string {
    const int16Array = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      const s = Math.max(-1, Math.min(1, float32[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    let binary = "";
    const bytes = new Uint8Array(int16Array.buffer);
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);

    // WAV Header 
    const headerBuffer = new ArrayBuffer(44);
    const view = new DataView(headerBuffer);
    const writeString = (view: DataView, offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) view.setUint8(offset + i, string.charCodeAt(i));
    };
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + bytes.byteLength, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, bytes.byteLength, true);

    // Concatenate header + data
    const wavBytes = new Uint8Array(headerBuffer.byteLength + bytes.byteLength);
    wavBytes.set(new Uint8Array(headerBuffer), 0);
    wavBytes.set(bytes, headerBuffer.byteLength);

    let binaryWav = "";
    for (let i = 0; i < wavBytes.length; i++) binaryWav += String.fromCharCode(wavBytes[i]);
    return btoa(binaryWav);
  }

  // Pause/resume VAD mic to prevent echo pickup
  const pauseVAD = useCallback(() => {
    if (vadRef.current) { try { vadRef.current.pause(); } catch { /* ignore */ } }
  }, []);

  const resumeVAD = useCallback(() => {
    if (vadRef.current) { try { vadRef.current.start(); } catch { /* ignore */ } }
  }, []);

  // Track active TTS stream sources for cancellation
  const ttsSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const ttsAbortRef = useRef<AbortController | null>(null);

  // Stop any currently playing AI audio (streaming or legacy)
  const stopAudio = useCallback(() => {
    // Cancel in-flight TTS fetch
    if (ttsAbortRef.current) {
      ttsAbortRef.current.abort();
      ttsAbortRef.current = null;
    }
    // Stop all scheduled AudioBufferSourceNodes
    for (const src of ttsSourcesRef.current) {
      try { src.stop(); } catch { /* already stopped */ }
    }
    ttsSourcesRef.current = [];
    nextStartTimeRef.current = 0;
    // Legacy HTMLAudioElement cleanup
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
  }, []);

  // Speak text using streaming Gemini TTS — plays audio chunks as they arrive
  // Used for initial greeting and fallback
  const speak = useCallback(async (text: string) => {
    if (stoppedRef.current) return;
    stopAudio(); pauseVAD();
    speakingRef.current = true;
    setVoiceState("speaking");

    const abort = new AbortController();
    ttsAbortRef.current = abort;

    try {
      const res = await fetch("/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, locale }),
        signal: abort.signal,
      });

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ error: "TTS request failed" }));
        throw new Error(err.error || "TTS request failed");
      }

      // Ensure AudioContext is ready
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }
      const ctx = audioContextRef.current;
      if (ctx.state === "suspended") await ctx.resume();
      nextStartTimeRef.current = 0;
      ttsSourcesRef.current = [];

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let lastSourceNode: AudioBufferSourceNode | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const { audio } = JSON.parse(line);
            if (!audio) continue;

            // Decode base64 PCM to Float32
            const raw = atob(audio);
            const bytes = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
            const int16 = new Int16Array(bytes.buffer);
            const float32 = new Float32Array(int16.length);
            for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;

            // Schedule playback immediately
            const audioBuffer = ctx.createBuffer(1, float32.length, 24000);
            audioBuffer.copyToChannel(float32, 0);
            const source = ctx.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(ctx.destination);

            const now = ctx.currentTime;
            const startTime = Math.max(now, nextStartTimeRef.current);
            source.start(startTime);
            nextStartTimeRef.current = startTime + audioBuffer.duration;

            ttsSourcesRef.current.push(source);
            lastSourceNode = source;
          } catch { /* skip malformed chunk */ }
        }
      }

      // When the last chunk finishes playing, resume VAD
      if (lastSourceNode) {
        // Safety timeout: if onended doesn't fire within expected time, force resume
        const expectedDuration = Math.max(0, nextStartTimeRef.current - ctx.currentTime);
        const safetyTimeout = setTimeout(() => {
          if (speakingRef.current) {
            console.warn("TTS onended didn't fire, forcing VAD resume");
            speakingRef.current = false;
            processingRef.current = false;
            ttsSourcesRef.current = [];
            resumeVAD();
            setVoiceState("listening");
          }
        }, (expectedDuration + 3) * 1000); // expected duration + 3s buffer

        lastSourceNode.onended = () => {
          clearTimeout(safetyTimeout);
          speakingRef.current = false;
          processingRef.current = false;
          ttsSourcesRef.current = [];
          resumeVAD();
          setVoiceState("listening");
        };
      } else {
        // No audio chunks received
        speakingRef.current = false;
        processingRef.current = false;
        resumeVAD();
        setVoiceState("listening");
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return; // intentional cancel
      console.error("Gemini TTS failed:", err);
      speakingRef.current = false;
      processingRef.current = false;
      resumeVAD();
      setVoiceState("listening");
    }
  }, [stopAudio, pauseVAD, resumeVAD, locale]);

  // Schedule a base64 PCM audio chunk for playback via AudioContext
  const scheduleAudioChunk = useCallback((audioBase64: string): AudioBufferSourceNode | null => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
    }
    const ctx = audioContextRef.current;
    if (ctx.state === "suspended") ctx.resume();

    try {
      const raw = atob(audioBase64);
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      const int16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;

      const audioBuffer = ctx.createBuffer(1, float32.length, 24000);
      audioBuffer.copyToChannel(float32, 0);
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      const now = ctx.currentTime;
      const startTime = Math.max(now, nextStartTimeRef.current);
      source.start(startTime);
      nextStartTimeRef.current = startTime + audioBuffer.duration;

      ttsSourcesRef.current.push(source);
      return source;
    } catch {
      return null;
    }
  }, []);

  // Send audio to the streaming voice API — text + TTS arrive in parallel
  const sendAudio = useCallback(
    async (audioBase64: string, mimeType: string) => {
      if (processingRef.current || stoppedRef.current || speakingRef.current) return;
      processingRef.current = true;
      // Cache for one-click retry if this call fails
      lastAudioRef.current = { audioBase64, mimeType };
      setLastError(null);
      pauseVAD();
      stopAudio();
      setVoiceState("processing"); setLoading(true);
      setStreamingUser(null);
      setStreamingReply(null);

      const abort = new AbortController();
      ttsAbortRef.current = abort;

      try {
        const res = await fetch("/api/interview-voice-stream", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            audioBase64, mimeType,
            history: historyRef.current,
            competencies, locale,
          }),
          signal: abort.signal,
        });

        if (!res.ok || !res.body) {
          const err = await res.json().catch(() => ({ error: "Stream request failed" }));
          throw new Error(err.error || "Stream request failed");
        }

        // Prepare AudioContext
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext({ sampleRate: 24000 });
        }
        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }
        nextStartTimeRef.current = 0;
        ttsSourcesRef.current = [];

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let ndjsonBuffer = "";
        let transcription = "";
        let replyParts: string[] = [];
        const lastSourceRef = { current: null as AudioBufferSourceNode | null };
        let isComplete = false;
        let firstAudioReceived = false;

        // Buffer for ordering out-of-order audio chunks
        const audioChunkBuffer: Map<number, string> = new Map();
        let nextAudioIndex = 0;

        // Process buffered audio chunks in order
        const flushAudioBuffer = () => {
          while (audioChunkBuffer.has(nextAudioIndex)) {
            const audioData = audioChunkBuffer.get(nextAudioIndex)!;
            audioChunkBuffer.delete(nextAudioIndex);
            if (!firstAudioReceived) {
              firstAudioReceived = true;
              speakingRef.current = true;
              setVoiceState("speaking");
            }
            const source = scheduleAudioChunk(audioData);
            if (source) lastSourceRef.current = source;
            nextAudioIndex++;
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          ndjsonBuffer += decoder.decode(value, { stream: true });
          const lines = ndjsonBuffer.split("\n");
          ndjsonBuffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim()) continue;

            // Step 1: parse JSON — swallow only malformed NDJSON lines here
            let msg: Record<string, unknown>;
            try {
              msg = JSON.parse(line);
            } catch {
              continue;
            }

            // Step 2: handle parsed message — let throws propagate to outer catch
            if (msg.type === "transcription") {
              transcription = msg.text as string;
              setStreamingUser(transcription);
              setLoading(false);
            } else if (msg.type === "inaudible") {
              setStreamingUser(null);
              setStreamingReply(null);
              const errMsg: ChatMessage = { role: "model", parts: [{ text: msg.reply as string }] };
              setHistory((prev) => [...prev, errMsg]);
              setLoading(false);
              processingRef.current = false;
              resumeVAD();
              setVoiceState("listening");
              return;
            } else if (msg.type === "text") {
              replyParts[msg.index as number] = msg.text as string;
              setStreamingReply(replyParts.filter(Boolean).join(" "));
            } else if (msg.type === "audio") {
              audioChunkBuffer.set(msg.index as number, msg.audio as string);
              flushAudioBuffer();
            } else if (msg.type === "done") {
              isComplete = msg.isComplete as boolean;
              if (sessionId && msg.usage) {
                fetch("/api/admin/update-session", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ sessionId, usage: msg.usage }),
                }).catch(() => {});
              }
            } else if (msg.type === "error") {
              throw new Error((msg.error as string) || "Stream error");
            }
          }
        }

        // EOF drain: if non-sequential audio chunks were left buffered (e.g. server skipped an
        // index or dropped a TTS chunk), play them in index order rather than leaving them
        // stuck. In the normal sequential case this loop is a no-op.
        if (audioChunkBuffer.size > 0) {
          const remainingIndices = [...audioChunkBuffer.keys()].sort((a, b) => a - b);
          console.warn(
            `[stream] EOF drain: ${remainingIndices.length} chunks remained buffered, ` +
            `last sequential index=${nextAudioIndex}, indices=${remainingIndices.join(",")}`
          );
          for (const i of remainingIndices) {
            const audioData = audioChunkBuffer.get(i)!;
            audioChunkBuffer.delete(i);
            if (!firstAudioReceived) {
              firstAudioReceived = true;
              speakingRef.current = true;
              setVoiceState("speaking");
            }
            const source = scheduleAudioChunk(audioData);
            if (source) lastSourceRef.current = source;
          }
        }

        // Stream finished — commit to history ONCE, clear streaming state
        const fullReply = replyParts.filter(Boolean).join(" ").replace("[INTERVIEW_COMPLETE]", "").trim();
        setStreamingUser(null);
        setStreamingReply(null);

        if (fullReply) {
          const userMsg: ChatMessage = { role: "user", parts: [{ text: transcription || "🎤" }] };
          const botMsg: ChatMessage = { role: "model", parts: [{ text: fullReply }] };
          const updated = [...historyRef.current, userMsg, botMsg];
          setHistory(updated);

          if (isComplete) {
            if (lastSourceRef.current) {
              // Race: if the source already finished playing before we set onended,
              // the callback never fires and the UI is stuck on "speaking". Pair onended
              // with a safety timeout based on the remaining audio duration. Whichever
              // fires first wins; the `finished` flag makes the path idempotent.
              let finished = false;
              const finish = () => {
                if (finished) return;
                finished = true;
                speakingRef.current = false;
                processingRef.current = false;
                stopVAD();
                safeOnComplete(updated);
              };
              lastSourceRef.current.onended = finish;
              const ctx = audioContextRef.current;
              const remainingMs = ctx
                ? Math.max(0, (nextStartTimeRef.current - ctx.currentTime) * 1000)
                : 0;
              setTimeout(finish, Math.min(remainingMs + 3000, 30000));
            } else {
              stopVAD();
              setTimeout(() => safeOnComplete(updated), 1000);
            }
            return;
          }
        }

        // Successful stream — drop cached audio so a later error doesn't retry a stale turn
        lastAudioRef.current = null;

        // Resume VAD when last audio chunk finishes playing
        if (lastSourceRef.current) {
          lastSourceRef.current.onended = () => {
            speakingRef.current = false;
            processingRef.current = false;
            ttsSourcesRef.current = [];
            resumeVAD();
            setVoiceState("listening");
          };
        } else {
          speakingRef.current = false;
          processingRef.current = false;
          resumeVAD();
          setVoiceState("listening");
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Streaming voice failed:", err);

        const errorPayload = {
          source: "InterviewChat.sendAudio",
          message: err instanceof Error ? err.message : String(err),
          name: err instanceof Error ? err.name : undefined,
          stack: err instanceof Error ? err.stack : undefined,
          locale,
          mimeType,
          audioBase64Len: audioBase64?.length ?? 0,
          historyLen: historyRef.current.length,
          competenciesCount: competencies.length,
          url: typeof window !== "undefined" ? window.location.href : undefined,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          clientTime: new Date().toISOString(),
        };
        fetch("/api/log-client-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(errorPayload),
          keepalive: true,
        }).catch(() => {});

        setStreamingUser(null);
        setStreamingReply(null);
        // Show as a transient banner with Retry — not persisted to history
        setLastError(t.chat.audioError);
        setLoading(false);
        processingRef.current = false;
        speakingRef.current = false;
        // Kill any still-playing AI audio before reopening mic — otherwise VAD
        // picks up speaker output and echoes back into the next sendAudio call.
        stopAudio();
        resumeVAD();
        setVoiceState("listening");
      }
    },
    [competencies, safeOnComplete, stopAudio, scheduleAudioChunk, pauseVAD, resumeVAD, locale]
  );

  // Retry the last failed audio payload (keeps the user's spoken answer intact)
  const handleRetry = useCallback(() => {
    const last = lastAudioRef.current;
    if (!last) return;
    setLastError(null);
    sendAudio(last.audioBase64, last.mimeType);
  }, [sendAudio]);

  // Manual "I'm done speaking" — flushes the rolling buffer as if VAD had fired
  // onSpeechEnd. Needed when background audio (TV, music) keeps VAD in speech
  // mode forever and onSpeechEnd never releases.
  const handleManualDone = useCallback(() => {
    if (speakingRef.current || processingRef.current) return;
    // Require at least ~300ms of buffered audio so a stray tap doesn't send silence.
    if (rollingSamplesRef.current < 16000 * 0.3) return;
    const totalLen = rollingSamplesRef.current;
    const merged = new Float32Array(totalLen);
    let offset = 0;
    for (const chunk of rollingBufferRef.current) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    rollingBufferRef.current = [];
    rollingSamplesRef.current = 0;
    const base64 = float32ToWavBase64(merged, 16000);
    sendAudio(base64, "audio/wav");
  }, [sendAudio]);

  // Initialize VAD
  async function startVAD() {
    try {
      if (typeof navigator === "undefined" || !navigator.mediaDevices) {
        console.error("getUserMedia not available — ensure HTTPS or localhost");
        return;
      }

      // Check microphone permission before initializing VAD
      try {
        // Try to get permission status first (not supported on all browsers)
        if (navigator.permissions) {
          const status = await navigator.permissions.query({ name: "microphone" as PermissionName });
          if (status.state === "denied") {
            setMicPermission("denied");
            setShowMicPopup(true);
            return;
          }
        }
        // Actually request mic access — this triggers the browser prompt if needed
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicPermission("granted");
      } catch (permErr) {
        // Permission denied or dismissed
        setMicPermission("denied");
        setShowMicPopup(true);
        return;
      }

      const { MicVAD } = await import("@ricky0123/vad-web");
      const vad = await MicVAD.new({
        onnxWASMBasePath: "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/",
        baseAssetPath: "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.29/dist/",
        model: "v5",
        onSpeechStart: () => {
          if (!speakingRef.current) setVoiceState("listening");
          // User starting to speak again is an implicit dismissal of the error banner
          setLastError(null);
        },
        onSpeechEnd: (audio: Float32Array) => {
          if (speakingRef.current || processingRef.current) return; // Don't process while AI is speaking
          // Reset the parallel rolling buffer — VAD handled the send.
          rollingBufferRef.current = [];
          rollingSamplesRef.current = 0;
          const base64 = float32ToWavBase64(audio, 16000);
          sendAudio(base64, "audio/wav");
        },
        onFrameProcessed: (_probs: { isSpeech: number; notSpeech: number }, frame: Float32Array) => {
          // RMS amplitude — bars animate on any sound, so a flat indicator
          // reveals a dead microphone (common on laptops with sleeping USB mics).
          let sum = 0;
          for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
          micLevelRef.current.level = Math.min(1, Math.sqrt(sum / frame.length) * 8);
          // Rolling buffer backing the manual "I'm done" button.
          if (speakingRef.current || processingRef.current) return;
          rollingBufferRef.current.push(new Float32Array(frame));
          rollingSamplesRef.current += frame.length;
          const MAX_SAMPLES = 16000 * 90; // 90s cap @ 16kHz
          while (rollingSamplesRef.current > MAX_SAMPLES && rollingBufferRef.current.length > 0) {
            const first = rollingBufferRef.current.shift()!;
            rollingSamplesRef.current -= first.length;
          }
        },
        redemptionMs: 3500, positiveSpeechThreshold: 0.6,
      });
      vadRef.current = vad;
      vad.start();
      setVoiceState("listening");
    } catch (err) { console.error("VAD init failed:", err); }
  }

  function stopVAD() {
    if (vadRef.current) { try { vadRef.current.pause(); vadRef.current.destroy(); } catch { } vadRef.current = null; }
    rollingBufferRef.current = [];
    rollingSamplesRef.current = 0;
    micLevelRef.current.level = 0;
    stopAudio();
    setVoiceState("idle");
  }

  async function startInterview() {
    setStarted(true);

    // LIVE MODE PATH
    if (isLiveMode) {
      const competencyList = competencies.map((c, i) => `${i + 1}. ${c.skill}: ${c.description}`).join("\n");
      const systemPrompt = getLiveSystemPrompt(competencyList, locale);

      liveHistoryRef.current = [];
      liveUserTextRef.current = "";
      try {
        await connectLive(systemPrompt);
        setVoiceState("live");
      } catch (err) {
        console.error("Live interview start failed:", err);
        posthog.capture("interview_start_failed", {
          interview_id: interviewId,
          mode: "live",
          error_class: classifyError(err),
          error_message: truncErrorMessage(err),
        });
        setStarted(false);
      }
      return;
    }

    // LEGACY PATH
    setLoading(true);
    try {
      const res = await fetch("/api/interview", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: "I am ready.", history: [], competencies, locale }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      const initUser: ChatMessage = { role: "user", parts: [{ text: t.chat.startMessage }] };
      const initBot: ChatMessage = { role: "model", parts: [{ text: data.reply }] };
      setHistory([initUser, initBot]);

      // Start VAD paused, speak with browser TTS, resume VAD when done
      await startVAD();
      pauseVAD();
      speak(data.reply);
    } catch (err) {
      console.error("Interview start failed:", err);
      posthog.capture("interview_start_failed", {
        interview_id: interviewId,
        mode: "standard",
        error_class: classifyError(err),
        error_message: truncErrorMessage(err),
      });
      setStarted(false);
    } finally { setLoading(false); }
  }

  if (!started) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-6 text-center select-none" style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}>
        <h2 className="text-2xl font-bold mb-4">{t.chat.topicsToCover}</h2>
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {competencies.map((c) => (
            <span key={c.skill} className="bg-brand/10 text-fg-link px-3 py-1 rounded-full text-sm">
              {c.skill}
            </span>
          ))}
        </div>

        {/* Live mode toggle hidden — only standard VAD + ElevenLabs path is shipped. */}

        <div className="flex items-center justify-center gap-3 flex-wrap">
          {(onCancel ?? onGoHome) && (
            <button
              onClick={onCancel ?? onGoHome}
              className="border border-line hover:border-line-strong text-fg hover:text-fg-heading font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              {t.chat.backBtn}
            </button>
          )}
          <button onClick={() => setShowGdprConsent(true)} className="bg-brand hover:bg-brand-fill-hover text-white font-semibold py-3 px-8 rounded-lg transition-colors">
            {t.chat.beginInterview}
          </button>
        </div>

        {/* GDPR consent modal */}
        {showGdprConsent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(var(--rgb-canvas),0.7)] backdrop-blur-sm px-4">
            <div className="bg-elevated border border-line rounded-2xl p-8 max-w-md w-full text-left shadow-2xl">
              <div className="w-16 h-16 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center text-3xl mx-auto mb-5">🔒</div>
              <h3 className="text-xl font-bold text-fg-heading mb-3 text-center">{t.chat.gdprTitle}</h3>
              <p className="text-fg-subtle text-sm leading-relaxed mb-4">
                {t.chat.gdprDesc}
              </p>
              <ul className="space-y-2 mb-6">
                {[t.chat.gdprPoint1, t.chat.gdprPoint2, t.chat.gdprPoint3, t.chat.gdprPoint4].map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-fg text-sm">
                    <span className="text-fg-link mt-0.5">✓</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/cookie-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fg-link hover:text-fg-heading text-xs underline block text-center mb-5"
              >
                {t.chat.gdprPrivacyLink}
              </a>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowGdprConsent(false);
                    setShowCameraPrompt(true);
                  }}
                  className="w-full bg-brand hover:bg-brand-fill-hover text-white font-semibold py-3 rounded-xl transition-colors"
                >
                  {t.chat.gdprAccept}
                </button>
                <button
                  onClick={() => setShowGdprConsent(false)}
                  className="w-full border border-line hover:border-line-strong text-fg hover:text-fg-heading py-2.5 rounded-xl transition-colors text-sm"
                >
                  {t.chat.gdprDecline}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Camera permission prompt modal */}
        {showCameraPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(var(--rgb-canvas),0.7)] backdrop-blur-sm px-4">
            <div className="bg-elevated border border-line rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
              <div className="w-16 h-16 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center text-3xl mx-auto mb-5">📹</div>
              <h3 className="text-xl font-bold text-fg-heading mb-3">{t.chat.cameraPromptTitle}</h3>
              <p className="text-fg-subtle text-sm leading-relaxed mb-6">
                {t.chat.cameraPromptDesc}
              </p>

              {webcamStatus === 'active' && (
                <div className="mb-4 rounded-xl overflow-hidden border border-success/30">
                  <video
                    ref={webcamAttachVideo}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-48 object-cover bg-sunken"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  <div className="bg-success/10 px-3 py-1.5 text-success text-xs font-medium">
                    ✓ {t.chat.cameraActive}
                  </div>
                </div>
              )}

              {webcamStatus === 'denied' && (
                <div className="bg-error/10 border border-error/20 rounded-xl p-4 mb-4 text-left space-y-2">
                  <p className="text-error text-sm font-medium">{t.chat.cameraDenied}</p>
                  <p className="text-fg-subtle text-xs">🖥️ Desktop: Click the 🔒 lock icon in your browser address bar → Site settings → Camera → Allow</p>
                  <p className="text-fg-subtle text-xs">📱 Mobile: Settings → Safari/Chrome → Camera → Allow for this site</p>
                </div>
              )}

              {webcamStatus === 'error' && webcamError && (
                <div className="bg-warning/10 border border-warning/20 rounded-xl p-3 mb-4">
                  <p className="text-warning text-sm">{webcamError}</p>
                </div>
              )}

              <div className="flex flex-col gap-3">
                {webcamStatus !== 'active' && (
                  <button
                    onClick={async () => {
                      await startCamera();
                    }}
                    disabled={webcamStatus === 'requesting'}
                    className="w-full bg-brand hover:bg-brand-fill-hover disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {webcamStatus === 'requesting' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t.chat.cameraRequesting}
                      </>
                    ) : (
                      <>📹 {t.chat.cameraEnable}</>
                    )}
                  </button>
                )}
                {webcamStatus === 'active' && (
                  <button
                    onClick={() => {
                      setShowCameraPrompt(false);
                      startInterview();
                    }}
                    className="w-full bg-brand hover:bg-brand-fill-hover text-white font-semibold py-3 rounded-xl transition-colors"
                  >
                    ✓ {t.chat.cameraStartInterview}
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowCameraPrompt(false);
                    startInterview();
                  }}
                  className="w-full border border-line hover:border-line-strong text-fg hover:text-fg-heading py-2.5 rounded-xl transition-colors text-sm"
                >
                  {t.chat.cameraSkip}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-2rem)] p-4 select-none" style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}>
      {/* Microphone permission popup */}
      {showMicPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(var(--rgb-canvas),0.7)] backdrop-blur-sm px-4">
          <div className="bg-elevated border border-line rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-error/10 border border-error/20 flex items-center justify-center text-3xl mx-auto mb-5">🎙️</div>
            <h3 className="text-xl font-bold text-fg-heading mb-3">Microphone Access Required</h3>
            <p className="text-fg-subtle text-sm leading-relaxed mb-6">
              This interview uses your microphone to hear your answers. Please allow microphone access when prompted by your browser.
            </p>
            <div className="bg-sunken rounded-xl p-4 mb-6 text-left space-y-2">
              <p className="text-fg text-xs font-medium">How to enable:</p>
              <p className="text-fg-subtle text-xs">🖥️ Desktop: Click the 🔒 lock icon in your browser address bar → Site settings → Microphone → Allow</p>
              <p className="text-fg-subtle text-xs">📱 Mobile: Settings → Safari/Chrome → Microphone → Allow for this site</p>
            </div>
            <button
              onClick={async () => {
                setShowMicPopup(false);
                setMicPermission("unknown");
                await startVAD();
              }}
              className="w-full bg-brand hover:bg-brand-fill-hover text-white font-semibold py-3 rounded-xl transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-line-subtle">
        {onGoHome && (
          <button onClick={onGoHome} className="flex items-center gap-1.5 mr-2 group" title="Home">
            <div className="w-7 h-7 rounded bg-brand flex items-center justify-center text-white font-bold text-sm group-hover:opacity-80 transition-opacity">
              A
            </div>
          </button>
        )}
        <span className="text-lg font-semibold">🎙️ {isLiveMode ? t.chat.liveInterview : t.chat.voiceInterview}</span>
        {timeLeft >= 0 && (
          <span className={`ml-2 text-sm font-mono px-2 py-0.5 rounded ${timeLeft <= 60 ? 'bg-error/15 text-error' : 'bg-sunken text-fg-subtle'}`}>
            {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, "0")}
          </span>
        )}
        <div className="flex gap-1 ml-auto">
          {competencies.map((c) => (
            <span
              key={c.skill}
              className="bg-sunken text-fg-subtle px-2 py-0.5 rounded text-xs"
            >
              {c.skill}
            </span>
          ))}
        </div>
      </div>

      {/* AI Avatar Section */}
      <div className="flex justify-center mb-4 gap-6 items-end">
        <div className="relative w-40 h-40 md:w-48 md:h-48 lg:w-56 lg:h-56">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/old_avatar.png"
            className="absolute inset-0 w-full h-full rounded-2xl object-cover shadow-2xl"
            alt="AI Interviewer"
          />
          <video
            ref={aiAvatarVideoCallbackRef}
            className={`absolute inset-0 w-full h-full rounded-2xl object-cover shadow-2xl transition-opacity duration-150 ${
              voiceState === "speaking" && videoReady ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src="/old_avatar.webm" type="video/webm" />
            <source src="/old_avatar.mp4" type="video/mp4" />
          </video>

          {voiceState === "speaking" && (
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-teal text-canvas text-xs font-medium px-3 py-1 rounded-full animate-pulse shadow-lg">
              Speaking...
            </div>
          )}
        </div>

        {/* Candidate Webcam Preview */}
        {webcamStatus === 'active' && (
          <div className="relative">
            <video
              ref={webcamAttachVideo}
              autoPlay
              playsInline
              muted
              className="w-40 h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 rounded-2xl object-cover bg-sunken shadow-2xl border-2 border-success/30"
              style={{ transform: 'scaleX(-1)' }}
            />
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-error text-canvas text-[10px] font-bold px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-canvas rounded-full animate-pulse" />
              REC
            </div>
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-elevated text-fg border border-line-subtle text-xs font-medium px-3 py-1 rounded-full shadow-lg">
              {t.chat.you}
            </div>
          </div>
        )}
      </div>

      {/* Mic activity + manual "I'm done" — only in standard mode where VAD runs.
          Also reveals a dead mic (flat bars) so users can fix it before wondering
          why the interviewer isn't responding. */}
      {!isLiveMode && started && (
        <div className="flex items-center justify-center gap-3 mb-4 flex-wrap px-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface border border-line-subtle">
            <MicLevelBars levelRef={micLevelRef} active={voiceState === "listening"} />
            <span className="text-xs text-fg-subtle tabular-nums">
              {voiceState === "listening" ? t.chat.micListening : "—"}
            </span>
          </div>
          <button
            type="button"
            onClick={handleManualDone}
            disabled={voiceState !== "listening"}
            className="text-xs px-3 py-1.5 rounded-full border border-line text-fg hover:border-brand hover:text-fg-link hover:bg-brand/10 disabled:opacity-40 disabled:hover:border-line disabled:hover:text-fg disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
            aria-label={t.chat.doneSpeakingBtn}
          >
            ✓ {t.chat.doneSpeakingBtn}
          </button>
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto space-y-4 pr-2"
        role="log"
        aria-label="Interview conversation"
      >
        {!isLiveMode && history.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] min-w-0 overflow-hidden rounded-lg px-4 py-3 ${msg.role === "user" ? "bg-brand text-white" : "bg-surface border border-line-subtle text-fg-heading"}`}>
              <div className={`text-xs mb-1 ${msg.role === "user" ? "text-white" : "text-fg-subtle"}`}>{msg.role === "user" ? t.chat.you : t.chat.interviewer}</div>
              <div dir="auto" className="prose prose-invert prose-sm max-w-none break-words [&_code]:break-all [&_code]:whitespace-pre-wrap [&_pre]:overflow-x-auto [&_pre]:max-w-full"><ReactMarkdown>{msg.parts[0].text}</ReactMarkdown></div>
            </div>
          </div>
        ))}
        {/* Streaming messages — shown during active stream, before committing to history */}
        {!isLiveMode && streamingUser && (
          <div className="flex justify-end">
            <div className="max-w-[80%] min-w-0 overflow-hidden rounded-lg px-4 py-3 bg-brand text-white">
              <div className="text-xs text-white mb-1">{t.chat.you}</div>
              <div dir="auto" className="prose prose-invert prose-sm max-w-none break-words [&_code]:break-all [&_code]:whitespace-pre-wrap [&_pre]:overflow-x-auto [&_pre]:max-w-full"><ReactMarkdown>{streamingUser}</ReactMarkdown></div>
            </div>
          </div>
        )}
        {!isLiveMode && streamingReply && (
          <div className="flex justify-start">
            <div className="max-w-[80%] min-w-0 overflow-hidden rounded-lg px-4 py-3 bg-surface border border-line-subtle text-fg-heading">
              <div className="text-xs text-fg-subtle mb-1">{t.chat.interviewer}</div>
              <div dir="auto" className="prose prose-invert prose-sm max-w-none break-words [&_code]:break-all [&_code]:whitespace-pre-wrap [&_pre]:overflow-x-auto [&_pre]:max-w-full"><ReactMarkdown>{streamingReply}</ReactMarkdown></div>
            </div>
          </div>
        )}
        {isLiveMode && (
          <>
            {/* Live mode status indicator */}
            <div className="flex items-center justify-center gap-3 py-4">
              <div className={`w-3 h-3 rounded-full ${liveState === 'connected' ? 'bg-success animate-pulse' : liveState === 'connecting' ? 'bg-warning animate-pulse' : 'bg-error'}`} />
              <span className={`text-sm ${liveState === 'connected' ? 'text-success' : liveState === 'connecting' ? 'text-warning' : 'text-error'}`}>
                {liveState === 'connected' ? t.chat.liveSpeak : liveState === 'connecting' ? t.chat.connecting : t.chat.disconnected}
              </span>
            </div>
            {/* Show transcript messages */}
            {history.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] min-w-0 overflow-hidden rounded-lg px-4 py-3 ${msg.role === "user" ? "bg-brand text-white" : "bg-surface border border-line-subtle text-fg-heading"}`}>
                  <div className={`text-xs mb-1 ${msg.role === "user" ? "text-white" : "text-fg-subtle"}`}>{msg.role === "user" ? t.chat.you : t.chat.interviewer}</div>
                  <div dir="auto" className="prose prose-invert prose-sm max-w-none break-words [&_code]:break-all [&_code]:whitespace-pre-wrap [&_pre]:overflow-x-auto [&_pre]:max-w-full"><ReactMarkdown>{msg.parts[0].text}</ReactMarkdown></div>
                </div>
              </div>
            ))}
            {/* Show currently streaming model text */}
            {liveModelText && (
              <div className="flex justify-start">
                <div className="max-w-[80%] min-w-0 overflow-hidden rounded-lg px-4 py-3 bg-surface border border-line-subtle text-fg-heading">
                  <div className="text-xs text-fg-subtle mb-1">{t.chat.interviewer}</div>
                  <div dir="auto" className="prose prose-invert prose-sm max-w-none break-words [&_code]:break-all [&_code]:whitespace-pre-wrap [&_pre]:overflow-x-auto [&_pre]:max-w-full">{liveModelText}<span className="animate-pulse">▊</span></div>
                </div>
              </div>
            )}
          </>
        )}
        {(loading || voiceState === "speaking") && !isLiveMode && (
          <div className="flex justify-start">
            <div className="bg-surface border border-line-subtle rounded-lg px-4 py-3 text-fg-subtle animate-pulse">
              {voiceState === "speaking" ? t.chat.aiSpeaking : voiceState === "processing" ? t.chat.processing : t.chat.thinking}
            </div>
          </div>
        )}
        {voiceState === "listening" && !isLiveMode && !loading && started && (
          <div className="flex justify-start">
            <div className="bg-success/10 border border-success/30 rounded-lg px-4 py-3 text-success flex items-center gap-3">
              <span>{t.chat.aiListening}</span>
              <MicLevelBars levelRef={micLevelRef} active={true} />
            </div>
          </div>
        )}
        {timeLeft >= 0 && timeLeft <= 60 && timeLeft > 0 && (
          <div className="flex justify-center">
            <div className="bg-warning/10 border border-warning/20 rounded-lg px-4 py-2 text-warning text-sm">
              ⏰ {timeLeft}s {t.chat.timeWarning}
            </div>
          </div>
        )}
        {showTimeCutoff && (
          <div className="flex justify-center">
            <div className="bg-error/10 border border-error/20 rounded-lg px-4 py-3 text-error text-sm text-center">
              {t.chat.timeCutoff}
            </div>
          </div>
        )}
        {lastError && (
          <div className="flex justify-center">
            <div className="bg-warning/10 border border-warning/30 rounded-lg px-4 py-3 text-warning text-sm max-w-md text-center">
              <div>{lastError}</div>
              <div className="mt-3 flex gap-2 justify-center">
                {lastAudioRef.current && (
                  <button
                    onClick={handleRetry}
                    className="bg-warning hover:opacity-90 text-canvas font-medium px-4 py-1.5 rounded-md transition-opacity"
                  >
                    {t.chat.retryBtn}
                  </button>
                )}
                <button
                  onClick={() => setLastError(null)}
                  className="border border-line hover:border-line-strong text-fg hover:text-fg-heading px-4 py-1.5 rounded-md transition-colors"
                >
                  {t.chat.dismissBtn}
                </button>
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="mt-4 flex items-center justify-center gap-4 py-4">
        <button
          onClick={() => {
            stoppedRef.current = true;
            // Stop all audio and mic first
            if (audioRef.current) {
              audioRef.current.onended = null;
              audioRef.current.onerror = null;
              audioRef.current.pause();
              audioRef.current.src = "";
              audioRef.current = null;
            }
            if (isLiveMode) {
              disconnectLive();
              if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
              }
              nextStartTimeRef.current = 0;
            } else {
              stopVAD();
            }
            processingRef.current = false;
            if (timerRef.current) clearInterval(timerRef.current);
            const finalHistory = isLiveMode ? liveHistoryRef.current : historyRef.current;
            posthog.capture("interview_end_clicked", {
              interview_id: interviewId,
              history_len: finalHistory.length,
              already_completed: completedRef.current,
              mode: isLiveMode ? "live" : "standard",
            });
            // Always fire — short/empty interviews still get a report attempt.
            safeOnComplete(finalHistory);
          }}
          className="bg-error hover:opacity-90 text-canvas font-semibold py-2 px-6 rounded-lg transition-opacity flex items-center gap-2"
        >
          <span>⏹</span> {t.chat.endInterview}
        </button>
      </div>
    </div>
  );
}
